import { urlRegex } from '@/constants/validation'
import { z } from 'zod'
import {
  eventHighlightSchema,
  eventImageSchema,
  eventPDFSchema,
  eventSponsorshipPackageSchema,
  eventTagSchema,
  eventTicketSchema,
  eventUserSchema,
  registrationBasedEventSchema,
} from './eventForm.schema'
import { MAX_EVENT_HIGHLIGHTS_LIMIT } from '@/constants'

export const baseEventFormSchema = z.object({
  eventName: z
    .string()
    .min(1, 'Event name is required')
    .max(255, 'Event name must be at most 255 characters'),
  description: z.string().min(1, 'Event description is required'),
  category: z.string().min(1, 'Category is required'),
  tag: z.array(eventTagSchema).max(10, 'Maximum 10 tags allowed'),
  eventImages: z
    .array(eventImageSchema)
    .min(1, 'At least one event image is required')
    .max(10, 'Maximum 10 event images allowed'),
  eventPDF: eventPDFSchema.optional().nullable(),
  eventInfoPDF: eventPDFSchema.optional().nullable(),
  startDate: z.date().optional(),
  startTime: z.date().optional(),
  endDate: z.date().optional(),
  endTime: z.date().optional(),
  timeZone: z
    .string({
      message: 'Time Zone is required',
    })
    .min(1, 'Time Zone is required'),
  registrationDeadline: z.date().optional(),
  sponsorshipPackages: z.array(eventSponsorshipPackageSchema),
  specialInstructions: z.string(),
  users: z.array(eventUserSchema),
  eventHighlights: z
    .array(eventHighlightSchema)
    .max(
      MAX_EVENT_HIGHLIGHTS_LIMIT,
      `Maximum ${MAX_EVENT_HIGHLIGHTS_LIMIT} event highlights allowed`
    ),
})

// Location-specific schemas using discriminated unions
export const onlineEventSchema = baseEventFormSchema.extend({
  location: z.literal('online'),
  zoomLink: z.string().min(1, 'Meeting link is required').regex(urlRegex, {
    message: 'Please enter a valid URL for meeting link',
  }),

  venueName: z.string().optional(),
  venueAddress: z.string().optional(),
  venueCity: z.string().optional(),
  venueState: z.string().optional(),
  venueCountry: z.string().optional(),
  venueLatitude: z.number().optional().nullable(),
  venueLongitude: z.number().optional().nullable(),
  venueZipCode: z.string().optional().nullable(),
})

export const offlineEventSchema = baseEventFormSchema.extend({
  location: z.literal('offline'),
  venueName: z
    .string()
    .min(1, 'Venue name is required')
    .max(255, 'Venue name must be at most 255 characters'),
  venueAddress: z
    .string()
    .min(1, 'Venue address is required')
    .max(255, 'Venue address must be at most 255 characters'),
  venueCity: z
    .string()
    .min(1, 'Venue city is required')
    .max(255, 'Venue city must be at most 255 characters'),
  venueState: z
    .string()
    .min(1, 'Venue state is required')
    .max(255, 'Venue state must be at most 255 characters'),
  venueCountry: z
    .string()
    .min(1, 'Venue country is required')
    .max(255, 'Venue country must be at most 255 characters'),
  venueLatitude: z
    .number({
      message: 'Venue Latitude is required',
    })
    .nullable(),
  venueLongitude: z
    .number({
      message: 'Venue Longitude is required',
    })
    .nullable(),
  venueZipCode: z.string().optional().nullable(),
  zoomLink: z.string().optional(),
})

export const hybridEventSchema = baseEventFormSchema.extend({
  location: z.literal('hybrid'),
  zoomLink: z.string().min(1, 'Meeting link is required').regex(urlRegex, {
    message: 'Please enter a valid URL for meeting link',
  }),
  venueName: z
    .string()
    .min(1, 'Venue name is required')
    .max(255, 'Venue name must be at most 255 characters'),
  venueAddress: z
    .string()
    .min(1, 'Venue address is required')
    .max(255, 'Venue address must be at most 255 characters'),
  venueCity: z
    .string()
    .min(1, 'Venue city is required')
    .max(255, 'Venue city must be at most 255 characters'),
  venueState: z
    .string()
    .min(1, 'Venue state is required')
    .max(255, 'Venue state must be at most 255 characters'),
  venueCountry: z
    .string()
    .min(1, 'Venue country is required')
    .max(255, 'Venue country must be at most 255 characters'),
  venueLatitude: z
    .number({
      message: 'Venue Latitude is required',
    })
    .nullable(),
  venueLongitude: z
    .number({
      message: 'Venue Longitude is required',
    })
    .nullable(),
  venueZipCode: z.string().optional().nullable(),
})

// Location union schema
export const locationBasedEventSchema = z.discriminatedUnion('location', [
  onlineEventSchema,
  offlineEventSchema,
  hybridEventSchema,
])

const eventDataSchema = locationBasedEventSchema.and(
  registrationBasedEventSchema
)

export const eventFormSchema = eventDataSchema

export type EventFormDataType = z.infer<typeof eventFormSchema>
export type EventTicketType = z.infer<typeof eventTicketSchema>
